import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { object, string } from 'yup'
import { Trans, useTranslation } from 'react-i18next'
import { StyleSheet, ViewProps } from 'react-native'
import { Draft, ZoneData, useZone } from 'zens-store'
import { Color, Spacing, Text } from 'zens-storybook'
import { Form, NavigationConfirm, BuildingField, FormProps } from '@dms/component'

const styles = StyleSheet.create({
  location: {
    minHeight: 78,
  },
  description: {
    zIndex: 200,
  },
  descriptionInput: {
    flex: 1,
    maxWidth: '100%',
  },
  optional: {
    color: Color.calmGrey,
  },
  help: {
    color: Color.metalGrey,
    marginTop: Spacing.XS,
    marginBottom: Spacing.XS,
  },
})

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  zoneId: number
  onSave?: (draft: Draft<ZoneData>) => Promise<void>
  onDelete?: (draft: Draft<ZoneData>) => Promise<void>
}
const ZoneEditForm = (props: Props) => {
  const { companyId, zoneId, onSave, onDelete, ...otherProps } = props
  const { t } = useTranslation()
  const zone = useZone({ companyId, zoneId })
  const draft = useMemo(() => new Draft<ZoneData>({ id: 0, company_id: companyId, ...zone.data?.data }), [zone.data])
  const { company_id } = draft.data

  const config = {
    groups: [
      {
        label: t('General information'),
        fields: [
          {
            key: 'name',
            label: t('Zone name'),
          },
          {
            key: 'description',
            label: (
              <>
                <Text level={5}>
                  <Trans>Description</Trans> <Text style={styles.optional}>({t('optional')})</Text>
                </Text>
                <Text level={5} style={styles.help}>
                  <Trans>
                    Describe the intended use for this zone. For example: used as dashboard filter, settings etcetera.
                  </Trans>
                </Text>
              </>
            ),
            fieldProps: {
              multiline: true,
              numberOfLines: 3,
              style: styles.descriptionInput,
              maxLength: 256,
            },
            style: styles.description,
          },
          {
            key: 'floor_id',
            label: t('Located at'),
            FieldInputComponent: BuildingField,
            fieldProps: {
              companyId: company_id,
              value: draft.data.building_id,
            },
            style: styles.location,
          },
        ],
      },
    ],
  }
  const schema = object({
    name: string().required(t('Zone name is required')),
  })
  return (
    <>
      <Form
        draft={draft}
        config={config}
        schema={schema}
        buttons={[
          [
            {
              title: t('Save'),
              onPress: onSave,
              validate: true,
            },
            {
              title: t('Cancel'),
              href: `/companies/${companyId}/workspaces/zones/${zoneId}`,
              light: true,
            },
          ],
          [
            {
              title: t('Delete zone'),
              onPress: onDelete,
              left: 'trash' as const,
              danger: true,
            },
          ],
        ]}
        {...otherProps}
      />
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()
  const config = {
    groups: [
      {
        label: t('General information'),
        fields: [
          {
            key: 'name',
            label: t('Zone name'),
          },
          {
            key: 'description',
            label: t('Description'),
            fieldProps: {
              multiline: true,
              style: styles.descriptionInput,
            },
            style: styles.description,
          },
          {
            key: 'floor_id',
            label: t('Located at'),
            FieldInputComponent: Text.Skeleton,
            style: styles.location,
          },
        ],
      },
    ],
  }
  return <Form.Skeleton config={config} numberOfbuttons={[2, 1]} {...props} />
}
ZoneEditForm.Skeleton = Skeleton

export default observer(ZoneEditForm)
